import { ApiErrorData, ApiResponse } from 'models/ApiResponse';
import {
  ActiveShift,
  AdminCityRow,
  AdminRegion,
  AdminUniversity,
  AdminUserDetail,
  BulkAssignLeadTagsInput,
  BulkAssignLeadTagsResponse,
  ExportLeadsParams,
  IncidentsResponse,
  LeadPaymentCatalogEntry,
  LeadProfileCatalogEntry,
  LeadTag,
  LeadTagWithCount,
  LeadsListParams,
  LeadsResponse,
  PatchProfileBasicInput,
  PatchProfileEvaluationInput,
  PatchProfileIdentityInput,
  PatchProfileOtherInput,
  PatchProfilePaymentInput,
  PatchProfilePaymentResponse,
  PatchProfileResponse,
  ProfileStatusEntry,
} from 'models/Lead';
import {
  BulkUpdateEnrollmentsInput,
  BulkUpdateEnrollmentsResponse,
  CreateTrainingInput,
  TrainingDetail,
  TrainingEnrollmentsResponse,
  TrainingsListParams,
  TrainingsListResponse,
  UpdateTrainingInput,
  UpdateTrainingStatusInput,
  UpdateTrainingStatusResponse,
} from 'models/Training';

import { FunctionsHelper } from 'utils/FunctionsHelper';
import MainBackendAxios from 'utils/MainBackendAxios';

const BASE = '/lead';

const getCountryCatalog = async (
  url: string,
  country: string
): Promise<ApiResponse<LeadPaymentCatalogEntry[] | ApiErrorData>> => {
  try {
    const response = await MainBackendAxios.get(url, { params: { country } });
    return {
      success: true,
      status: response.status,
      data: response?.data?.data,
    };
  } catch (err: any) {
    return FunctionsHelper.getErrorFormat(err);
  }
};

export const LeadService = {
  getShifts: async (): Promise<ApiResponse<ActiveShift[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/shifts`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getRegions: async (
    country: string
  ): Promise<ApiResponse<AdminRegion[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/regions`, {
        params: { country },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getCities: async (
    country: string,
    regionId?: string
  ): Promise<ApiResponse<AdminCityRow[] | ApiErrorData>> => {
    try {
      const params: Record<string, any> = { country };
      // `regionId` es singular y no admite vacío: sólo se manda si tiene valor.
      if (regionId) {
        params.regionId = regionId;
      }

      const response = await MainBackendAxios.get(`${BASE}/cities`, {
        params,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getUniversities: async (
    country: string
  ): Promise<ApiResponse<AdminUniversity[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/universities`, {
        params: { country },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getProfileStatuses: async (): Promise<
    ApiResponse<ProfileStatusEntry[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/profile-statuses`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getNationalities: async (): Promise<
    ApiResponse<LeadProfileCatalogEntry[] | ApiErrorData>
  > => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/nationalities`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getGenders: async (
    country: string
  ): Promise<ApiResponse<LeadProfileCatalogEntry[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/genders`, {
        params: { country },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getMaritalStatuses: async (
    country: string
  ): Promise<ApiResponse<LeadProfileCatalogEntry[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/marital-statuses`, {
        params: { country },
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getLeads: async (
    params: LeadsListParams
  ): Promise<ApiResponse<LeadsResponse | ApiErrorData>> => {
    try {
      const queryParams: Record<string, any> = {
        country: params.country,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sort: params.sort ?? 'fullName',
        dir: params.dir ?? 'asc',
      };

      if (params.q && params.q.trim().length > 0) {
        queryParams.q = params.q.trim();
      }

      if (params.states && params.states.length > 0) {
        queryParams.states = params.states;
      }

      if (params.universityId !== undefined) {
        // Forzamos string para que axios nunca lo omita ni lo serialice raro.
        // null → "null" (significa "Otros" para el backend);
        // un id numérico → su string equivalente.
        queryParams.universityId = String(params.universityId);
      }

      // Los filtros plurales van como parámetro repetido (?k=a&k=b), nunca CSV:
      // el backend interpreta "1,2" como un único valor inválido.
      if (params.universityIds && params.universityIds.length > 0) {
        // null → "null" (Otros), igual que en el singular.
        queryParams.universityIds = params.universityIds.map((id) =>
          String(id)
        );
      }

      if (params.regionIds && params.regionIds.length > 0) {
        // Sin coerción numérica: el zero-padding ("05" !== "5") es significativo.
        queryParams.regionIds = params.regionIds;
      }

      if (params.cityIds && params.cityIds.length > 0) {
        queryParams.cityIds = params.cityIds.map((id) => String(id));
      }

      if (params.tagIds && params.tagIds.length > 0) {
        queryParams.tagIds = params.tagIds.map((id) => String(id));
        if (params.tagMatch) {
          queryParams.tagMatch = params.tagMatch;
        }
      }

      if (params.nationalityIds && params.nationalityIds.length > 0) {
        queryParams.nationalityIds = params.nationalityIds.map((id) =>
          String(id)
        );
      }

      if (params.genderIds && params.genderIds.length > 0) {
        queryParams.genderIds = params.genderIds.map((id) => String(id));
      }

      if (params.maritalStatusIds && params.maritalStatusIds.length > 0) {
        queryParams.maritalStatusIds = params.maritalStatusIds.map((id) =>
          String(id)
        );
      }

      if (params.paymentConfigured !== undefined) {
        // Upstream only accepts the lowercase strings "true" / "false".
        queryParams.paymentConfigured = String(params.paymentConfigured);
      }

      const response = await MainBackendAxios.get(`${BASE}/leads`, {
        params: queryParams,
        paramsSerializer: { indexes: false },
      });

      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getById: async (
    id: string
  ): Promise<ApiResponse<AdminUserDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/users/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateProfileBasic: async (
    id: string,
    body: Omit<PatchProfileBasicInput, 'section'>
  ): Promise<ApiResponse<PatchProfileResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/profiles/${id}`, {
        section: 'basic',
        ...body,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateProfileIdentity: async (
    id: string,
    body: Omit<PatchProfileIdentityInput, 'section'>
  ): Promise<ApiResponse<PatchProfileResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/profiles/${id}`, {
        section: 'identity',
        ...body,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateProfileEvaluation: async (
    id: string,
    body: Omit<PatchProfileEvaluationInput, 'section'>
  ): Promise<ApiResponse<PatchProfileResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/profiles/${id}`, {
        section: 'evaluation',
        ...body,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateProfileOther: async (
    id: string,
    body: Omit<PatchProfileOtherInput, 'section'>
  ): Promise<ApiResponse<PatchProfileResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/profiles/${id}`, {
        section: 'other',
        ...body,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateProfilePayment: async (
    id: string,
    body: Omit<PatchProfilePaymentInput, 'section'>
  ): Promise<ApiResponse<PatchProfilePaymentResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(`${BASE}/profiles/${id}`, {
        section: 'payment',
        ...body,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  // Payment catalogs ("Datos de pago"); `country` is required upstream.
  getBanks: async (
    country: string
  ): Promise<ApiResponse<LeadPaymentCatalogEntry[] | ApiErrorData>> =>
    getCountryCatalog(`${BASE}/banks`, country),

  getBankAccountTypes: async (
    country: string
  ): Promise<ApiResponse<LeadPaymentCatalogEntry[] | ApiErrorData>> =>
    getCountryCatalog(`${BASE}/bank-account-types`, country),

  getPensionFunds: async (
    country: string
  ): Promise<ApiResponse<LeadPaymentCatalogEntry[] | ApiErrorData>> =>
    getCountryCatalog(`${BASE}/pension-funds`, country),

  getHealthProviders: async (
    country: string
  ): Promise<ApiResponse<LeadPaymentCatalogEntry[] | ApiErrorData>> =>
    getCountryCatalog(`${BASE}/health-providers`, country),

  updateAvatar: async (
    id: string,
    file: File
  ): Promise<ApiResponse<{ avatarUrl: string | null } | ApiErrorData>> => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await MainBackendAxios.patch(
        `${BASE}/profiles/${id}/avatar`,
        formData
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  clearAvatar: async (
    id: string
  ): Promise<ApiResponse<{ avatarUrl: string | null } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/profiles/${id}/avatar`
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  exportLeads: async (
    params: ExportLeadsParams
  ): Promise<ApiResponse<{ blob: Blob; filename: string } | ApiErrorData>> => {
    try {
      const queryParams: Record<string, any> = {
        country: params.country,
      };
      if (params.timezone) queryParams.timezone = params.timezone;
      if (params.createdFrom) queryParams.createdFrom = params.createdFrom;
      if (params.createdTo) queryParams.createdTo = params.createdTo;
      if (params.availabilityUpdatedFrom) {
        queryParams.availabilityUpdatedFrom = params.availabilityUpdatedFrom;
      }
      if (params.availabilityUpdatedTo) {
        queryParams.availabilityUpdatedTo = params.availabilityUpdatedTo;
      }
      if (params.statuses && params.statuses.length > 0) {
        queryParams.statuses = params.statuses;
      }

      // Filtros plurales: parámetro repetido (?k=a&k=b), nunca CSV. Misma
      // semántica y cotas que en el listado.
      if (params.universityIds && params.universityIds.length > 0) {
        // null → "null" (leads sin universidad del catálogo, "Otros").
        queryParams.universityIds = params.universityIds.map((id) =>
          String(id)
        );
      }

      if (params.regionIds && params.regionIds.length > 0) {
        // Sin coerción numérica: el zero-padding ("05" !== "5") es significativo.
        queryParams.regionIds = params.regionIds;
      }

      if (params.cityIds && params.cityIds.length > 0) {
        queryParams.cityIds = params.cityIds.map((id) => String(id));
      }

      if (params.tagIds && params.tagIds.length > 0) {
        queryParams.tagIds = params.tagIds.map((id) => String(id));
        if (params.tagMatch) {
          queryParams.tagMatch = params.tagMatch;
        }
      }

      const response = await MainBackendAxios.get(`${BASE}/leads/export`, {
        params: queryParams,
        paramsSerializer: { indexes: false },
        responseType: 'blob',
      });

      let filename = `leads-${params.country}.xlsx`;
      const contentDispositionHeader = response.headers['content-disposition'];
      if (contentDispositionHeader) {
        const matches = contentDispositionHeader.match(/filename="?([^"]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      return {
        success: true,
        status: response.status,
        data: { blob: response.data, filename },
      };
    } catch (err: any) {
      // El backend manda JSON en errores, pero axios trae blob → convertir
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch (parseErr) {
          // si no es JSON, dejar como estaba
        }
      }
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getTags: async (
    country: string,
    q?: string,
    limit?: number
  ): Promise<ApiResponse<LeadTagWithCount[] | ApiErrorData>> => {
    try {
      const params: Record<string, any> = { country };
      if (q && q.trim().length > 0) {
        params.q = q.trim();
      }
      if (limit !== undefined) {
        params.limit = limit;
      }

      const response = await MainBackendAxios.get(`${BASE}/tags`, { params });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateLeadTags: async (
    id: string,
    tags: string[]
  ): Promise<ApiResponse<LeadTag[] | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.put(`${BASE}/users/${id}/tags`, {
        tags,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  bulkAssignTags: async (
    input: BulkAssignLeadTagsInput
  ): Promise<ApiResponse<BulkAssignLeadTagsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(
        `${BASE}/tags/bulk-assign`,
        input
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getIncidents: async (
    profileId: string
  ): Promise<ApiResponse<IncidentsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/profiles/${profileId}/incidents`
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  createTraining: async (
    body: CreateTrainingInput
  ): Promise<ApiResponse<TrainingDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.post(`${BASE}/trainings`, body);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getTrainings: async (
    params: TrainingsListParams
  ): Promise<ApiResponse<TrainingsListResponse | ApiErrorData>> => {
    try {
      const queryParams: Record<string, any> = {
        countryCode: params.countryCode,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        sort: params.sort ?? 'starts_at',
        dir: params.dir ?? 'desc',
      };

      if (params.kind) queryParams.kind = params.kind;
      if (params.status) queryParams.status = params.status;
      if (params.startsFrom) queryParams.startsFrom = params.startsFrom;
      if (params.startsTo) queryParams.startsTo = params.startsTo;

      const response = await MainBackendAxios.get(`${BASE}/trainings`, {
        params: queryParams,
      });
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getTrainingById: async (
    id: number
  ): Promise<ApiResponse<TrainingDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(`${BASE}/trainings/${id}`);
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateTraining: async (
    id: number,
    body: UpdateTrainingInput
  ): Promise<ApiResponse<TrainingDetail | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/trainings/${id}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  updateTrainingStatus: async (
    id: number,
    body: UpdateTrainingStatusInput
  ): Promise<ApiResponse<UpdateTrainingStatusResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/trainings/${id}/status`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  getTrainingEnrollments: async (
    id: number
  ): Promise<ApiResponse<TrainingEnrollmentsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/trainings/${id}/enrollments`
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  exportTrainingEnrollments: async (
    id: number
  ): Promise<ApiResponse<{ blob: Blob; filename: string } | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.get(
        `${BASE}/trainings/${id}/enrollments/export`,
        { responseType: 'blob' }
      );

      let filename = `participants-${id}.xlsx`;
      const contentDispositionHeader = response.headers['content-disposition'];
      if (contentDispositionHeader) {
        const matches = contentDispositionHeader.match(/filename="?([^"]+)"?/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      return {
        success: true,
        status: response.status,
        data: { blob: response.data, filename },
      };
    } catch (err: any) {
      // El backend manda JSON en errores, pero axios trae blob → convertir
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          err.response.data = JSON.parse(text);
        } catch (parseErr) {
          // si no es JSON, dejar como estaba
        }
      }
      return FunctionsHelper.getErrorFormat(err);
    }
  },

  bulkUpdateEnrollments: async (
    id: number,
    body: BulkUpdateEnrollmentsInput
  ): Promise<ApiResponse<BulkUpdateEnrollmentsResponse | ApiErrorData>> => {
    try {
      const response = await MainBackendAxios.patch(
        `${BASE}/trainings/${id}/enrollments`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: response?.data?.data,
      };
    } catch (err: any) {
      return FunctionsHelper.getErrorFormat(err);
    }
  },
};
